// @flow

import R from 'ramda';
import {
  DRUID_TO_HUMAN_READABLE,
  CORE_EVENT_NAMES,
  STANDARD_EVENT_NAMES,
  FILTER_DIMENSIONS,
  CONDITIONAL_DRUID_TO_HUMAN_READABLE
} from '../../../support/eo_mapping';
import { SK_ADNETWORK_DATASOURCE } from '../../../support/eo_mapping/datasources';
import {
  getCustomEventName,
  getLabelFromEventDescription,
  isCustomEvent,
  parseEventDescriptionFromEventKeyOrUndefined
} from '../../../support/utils';

export const DEFAULT_DRUID_FILTERS_LIST = [
  'attributed',
  'user_data_platform',
  'last_attributed_touch_data_tilde_campaign',
  'last_attributed_touch_data_tilde_channel',
  'last_attributed_touch_data_tilde_feature',
  'last_attributed_touch_data_tilde_stage',
  'last_attributed_touch_data_tilde_tags',
  'last_attributed_touch_data_tilde_advertising_partner_name',
  'last_attributed_touch_data_dollar_3p',
  'last_attributed_touch_data_tilde_ad_name',
  'last_attributed_touch_data_tilde_secondary_publisher',
  'last_attributed_touch_data_tilde_creative_name',
  'last_attributed_touch_data_tilde_ad_set_name',
  'last_attributed_touch_data_tilde_id',
  'last_attributed_touch_data_plus_web_format',
  'last_attributed_touch_type',
  'last_attributed_touch_data_tilde_keyword',
  'user_data_geo_country_code',
  'user_data_os',
  'last_attributed_touch_data_tilde_customer_ad_name',
  'last_attributed_touch_data_tilde_customer_ad_set_name',
  'last_attributed_touch_data_tilde_customer_campaign',
  'last_attributed_touch_data_tilde_customer_keyword',
  'last_attributed_touch_data_tilde_customer_placement',
  'last_attributed_touch_data_tilde_customer_secondary_publisher',
  'last_attributed_touch_data_tilde_ad_id',
  'last_attributed_touch_data_tilde_ad_set_id',
  'last_attributed_touch_data_tilde_campaign_id',
  'last_attributed_touch_data_tilde_placement',
  'last_attributed_touch_data_tilde_sub_site_name',
  'last_attributed_touch_data_tilde_customer_sub_site_name',
  'last_attributed_touch_data_tilde_agency_id',
  'last_attributed_touch_data_tilde_agency',
  'user_data_geo_dma_code',
  'user_data_brand',
  'user_data_device_type',
  'user_data_model',
  'user_data_geo_country_en',
  'user_data_geo_region_en',
  'customer_event_alias',
  'first_event_for_user',
  'dismissal_source'
];

/*
 * Maps an array of EO key strings to an array of objects, each with 'label'
 * and 'value' properties, where the label is the display name for the
 * corresponding key.
 *
 * @param {array} Array of EO key strings
 * @return {array} Array of objects, each with 'label' and 'value' properties,
 * where the label is the display name corresponding to that key
 */
export const toSelectFormatEventDatasourceMap = R.map(option => {
  if (isCustomEvent(option)) {
    return {
      label: getCustomEventName(option),
      value: option
    };
  }

  // using datasource as value (i.e. eo_install)
  // using human readable name as label (i.e. installs)
  if (CORE_EVENT_NAMES[option]) {
    const coreEventDatasource = CORE_EVENT_NAMES[option];
    return {
      value: coreEventDatasource,
      label: DRUID_TO_HUMAN_READABLE[coreEventDatasource]
    };
  }

  if (option === SK_ADNETWORK_DATASOURCE) {
    return {
      value: option,
      label: DRUID_TO_HUMAN_READABLE[option]
    };
  }

  const eventDescription = parseEventDescriptionFromEventKeyOrUndefined(option);
  if (eventDescription) {
    return {
      value: option,
      label: getLabelFromEventDescription(
        eventDescription,
        DRUID_TO_HUMAN_READABLE
      )
    };
  }

  // can't use datasource as value since multiple event names per datasource
  // currently not mapping to human readable labels
  return {
    value: option,
    label: option
  };
});

/*
 * Identical to toSelectFormatEventDatasourceMap with 2 differences:
 * 1. custom events are not prepended with eo_custom_event
 * 2. core events are not translated to their eo names (keep 'OPEN' as 'OPEN' instead of 'eo_open')
 */
export const toSelectFormatCohortEventDatasourceMap = R.map(option => {
  return {
    value: option,
    label: option
  };
});

export const toSelectFormatDimensionMap = (
  option: string,
  labelMap?: Object = DRUID_TO_HUMAN_READABLE
) => {
  const label = labelMap[option] || getCustomEventName(option);

  return { label, value: option };
};

export function toSelectValue(
  value: string,
  labelMap?: Object = FILTER_DIMENSIONS,
  unclearableValue?: boolean = false,
  dimension?: string
) {
  let label = labelMap[value] || value;

  if (
    dimension &&
    Object.keys(CONDITIONAL_DRUID_TO_HUMAN_READABLE).includes(dimension)
  ) {
    label = CONDITIONAL_DRUID_TO_HUMAN_READABLE[dimension][value] || label;
  }

  // make date_range unclearable in analytics table CompareBy dropdowns
  if (value === 'date_range' || unclearableValue) {
    return { label, value, unclearableValue: true };
  }

  return { label, value };
}

/*
 * Translates all filter options from just arrays of EO strings to arrays of
 * objects each with a display label.
 *
 * @param {object} Object with a property for each potential filter, the value
 * of which is an array of EO strings representing each option for that filter.
 * @return {object} Object with same original keys, but each value is now an
 * array of objects with display name 'label' and 'value' properties.
 */
export const formatFilterOptions = (filters: Object, labelMap?: Object) => {
  const filterArrays = Object.values(filters);
  const filterKeys = Object.keys(filters);
  return filterArrays.reduce((arr: {}, values: any, index: number) => {
    const key = filterKeys[index];

    const map = labelMap || CONDITIONAL_DRUID_TO_HUMAN_READABLE[key];

    arr[key] = values.map(filterOption =>
      toSelectFormatDimensionMap(filterOption, map)
    );

    return arr;
  }, {});
};

/**
 * turns and object containing druid filter types as keys into select format
 * @param {Object} filters
 * @return {Array} react-select formatted list of filter type options
 */
export const objectKeysToSelectFormat = (
  filters: Object,
  labelMap?: Object
) => {
  return Object.keys(filters).map(dimension => {
    return toSelectFormatDimensionMap(dimension, labelMap);
  });
};

export const EMPTY_ROW = {
  dimension: '',
  comparator: 'equals',
  values: []
};

export const filterArrayToObject = R.reduce((acc, curr) => {
  const values = R.map(option => option.value, curr.values);

  acc[curr.dimension] = values;
  return acc;
}, {});

export const getDataSourcesFromEvent = (event: string) => {
  let dataSource = STANDARD_EVENT_NAMES[event];
  if (!dataSource) {
    dataSource = isCustomEvent(event) ? 'eo_custom_event' : event;
  }
  return dataSource;
};

// sort dropdown options alphabetically by label
export const sortByLabel = (dropdownOptions: Array<Object>) =>
  dropdownOptions.sort((a, b) =>
    a.label.toUpperCase() < b.label.toUpperCase() ? -1 : 1
  );
